import React, { useState, useEffect, useCallback } from 'react';
import { Search, Wrench, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/common/Toast';
import ToolCard from '../components/tools/ToolCard';
import Modal from '../components/common/Modal';
import './Tools.css';

function Tools() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTool, setDeleteTool] = useState(null);

  const canManage = user?.role === 'admin' || user?.role === 'manager';
  const showCosts = canManage;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (categoryFilter) params.set('category_id', categoryFilter);
      if (statusFilter) params.set('status', statusFilter);

      const [toolsRes, categoriesRes] = await Promise.allSettled([
        api.get(`/tools?${params.toString()}`),
        api.get('/tools/categories'),
      ]);

      setTools(toolsRes.status === 'fulfilled' ? (toolsRes.value.tools || []) : []);
      setCategories(categoriesRes.status === 'fulfilled' ? (categoriesRes.value.categories || []) : []);
    } catch (err) {
      // Don't loop
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, statusFilter]);

  useEffect(() => {
    const timeout = setTimeout(loadData, 300);
    return () => clearTimeout(timeout);
  }, [loadData]);

  const handleEdit = (tool) => {
    navigate('/admin/tools', { state: { editTool: tool } });
  };

  const handleDeleteClick = (tool) => {
    setDeleteTool(tool);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTool) return;
    try {
      await api.delete(`/tools/${deleteTool.id}`);
      toast.success(`${deleteTool.name} deleted`);
      setDeleteModalOpen(false);
      setDeleteTool(null);
      loadData();
    } catch (err) {
      toast.error('Failed to delete tool');
    }
  };

  return (
    <div className="tools-page">
      <div className="page-header">
        <Wrench size={22} strokeWidth={2} />
        <h1>Tools Directory</h1>
        {canManage && (
          <button className="btn-primary" onClick={() => navigate('/admin/tools')}>
            <Plus size={16} />
            Manage Tools
          </button>
        )}
      </div>

      <div className="tools-filters">
        <div className="search-input-wrap">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="evaluating">Evaluating</option>
          <option value="deprecated">Deprecated</option>
        </select>
      </div>

      {loading ? (
        <div className="tools-loading">Loading tools...</div>
      ) : tools.length === 0 ? (
        <div className="tools-empty">
          <Wrench size={32} strokeWidth={1.5} />
          {(searchQuery || categoryFilter || statusFilter) ? (
            <>
              <p>No tools match your filters</p>
              <button
                className="btn-link"
                onClick={() => { setSearchQuery(''); setCategoryFilter(''); setStatusFilter(''); }}
              >
                Clear filters
              </button>
            </>
          ) : canManage ? (
            <p>No tools have been added yet. Click "Manage Tools" to add your first tool.</p>
          ) : (
            <p>No tools have been added yet.</p>
          )}
        </div>
      ) : (
        <div className="tools-grid">
          {tools.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              canManage={canManage}
              showCosts={showCosts}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteTool(null); }}
        title="Delete Tool"
        size="small"
      >
        <p style={{ marginBottom: '16px', color: '#374151' }}>
          Are you sure you want to delete <strong>{deleteTool?.name}</strong>? This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={() => { setDeleteModalOpen(false); setDeleteTool(null); }}>
            Cancel
          </button>
          <button className="btn-danger" onClick={handleDeleteConfirm}>
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Tools;
